import {
  Component,
  Input,
  Output,
  EventEmitter,
  ViewChild,
  ElementRef,
  OnInit,
  OnDestroy,
  HostListener,
  OnChanges,
} from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { Subject } from 'rxjs';
import { takeUntil, debounceTime, distinctUntilChanged } from 'rxjs/operators';
import {
  INoticeBoardPosts,
  IPostComments,
  IPostLikes,
} from 'src/app/models/notice-board';
import { UserRegDetails } from 'src/app/models/user';
import { ReportPostDialogComponent } from '../report-post-dialog/report-post-dialog.component';

@Component({
  selector: 'app-post-card',
  templateUrl: './post-card.component.html',
  styleUrls: ['./post-card.component.scss'],
})
export class PostCardComponent implements OnInit, OnDestroy {
  @Input() post!: INoticeBoardPosts;
  @Input() highlightedPostId: string | null = null;
  @Input() loggedIsUsernameProfile: string = '';
  @Input() userOrgList: UserRegDetails[] = [];
  @Input() currentUserId: string = '';
  @Input() deviceType: 'mobile' | 'tablet' | 'desktop' = 'desktop';
  @Output() onPostLike = new EventEmitter<any>();
  @Output() onPostComment = new EventEmitter<{
    post: any;
    comment: string;
    commentMentioned?: UserRegDetails[];
  }>();
  @Output() onPostShare = new EventEmitter<string>();
  @Output() onReportPost = new EventEmitter<{
    postId: string;
    reportReason: string;
    reportDescription: string;
  }>();
  @Output() onSavePost = new EventEmitter<string>();
  @Output() onHidePost = new EventEmitter<string>();
  @Output() openSharedPostPoll = new EventEmitter<string>();

  @ViewChild('commentTextarea', { static: false })
  commentTextarea!: ElementRef<HTMLTextAreaElement>;

  // Component state
  isMenuOpen = false;
  showImageModal = false;
  showAllComments = false;
  isCommentFocused = false;
  commentText = '';
  textareaHeight = 20;
  isUserOnline = false;
  defaultAvatar = 'assets/images/default-avatar.png';

  // User mention functionality
  showUserList = false;
  filteredUserOrgList: UserRegDetails[] = [];
  selectedIndex = 0;
  mentionStartIndex = 0;
  mentionedUsers: UserRegDetails[] = [];

  private destroy$ = new Subject<void>();
  private commentInput$ = new Subject<string>();

  constructor(private dialog: MatDialog) {}

  ngOnInit(): void {
    // Set user online status (mock implementation)
    // this.isUserOnline = true;

    // Setup comment input debouncing for user mentions
    this.commentInput$
      .pipe(debounceTime(300), distinctUntilChanged(), takeUntil(this.destroy$))
      .subscribe((value) => {
        this.handleUserMention(value);
      });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: Event): void {
    const target = event.target as HTMLElement;
    const menuContainer = target.closest('.menu-container');

    // Only close menu if click is outside menu container
    if (this.isMenuOpen && !menuContainer) {
      this.closeMenu();
    }

    // Handle user suggestions
    if (this.showUserList) {
      const suggestionContainer = target.closest('.user-suggestions');
      if (!suggestionContainer) {
        this.hideUserSuggestions();
      }
    }
  }

  @HostListener('document:keydown', ['$event'])
  onDocumentKeyDown(event: KeyboardEvent): void {
    if (this.showUserList) {
      this.handleUserSuggestionNavigation(event);
    }
  }

  // Card styling methods
  getCardClass(): string {
    let classes = this.deviceType;
    if (this.highlightedPostId && this.post.postId === this.highlightedPostId) {
      classes += ' highlighted';
    }
    return classes;
  }

  // Date formatting
  getFormattedDateTime(date: Date): string {
    if (!date) return '';

    const now = new Date();
    const postDate = new Date(date + 'Z');
    const diffInMinutes = Math.floor(
      (now.getTime() - postDate.getTime()) / (1000 * 60)
    );

    if (diffInMinutes < 1) return 'Just now';
    if (diffInMinutes < 60) return `${diffInMinutes}m ago`;

    const diffInHours = Math.floor(diffInMinutes / 60);
    if (diffInHours < 24) return `${diffInHours}h ago`;

    const diffInDays = Math.floor(diffInHours / 24);
    if (diffInDays < 7) return `${diffInDays}d ago`;

    // Format as DD/MM/YY, hh:mm AM/PM
    const day = postDate.getDate().toString().padStart(2, '0');
    const month = (postDate.getMonth() + 1).toString().padStart(2, '0');
    const year = postDate.getFullYear().toString().slice(-2);

    let hours = postDate.getHours();
    const minutes = postDate.getMinutes().toString().padStart(2, '0');
    const ampm = hours >= 12 ? 'PM' : 'AM';
    hours = hours % 12 || 12;

    const formattedTime = `${hours}:${minutes} ${ampm}`;
    return `${day}/${month}/${year}, ${formattedTime}`;
  }

  // Menu functionality
  toggleMenu(event?: Event): void {
    if (event) {
      event.stopPropagation(); // Prevent event bubbling
    }
    this.isMenuOpen = !this.isMenuOpen;
  }

  closeMenu(): void {
    this.isMenuOpen = false;
  }

  reportPost(event?: Event): void {
    if (event) {
      event.stopPropagation();
    }
    this.closeMenu();
    console.log(this.post, 'cehck me ');
    const dialogRef = this.dialog.open(ReportPostDialogComponent, {
      width: '400px', // You can set a specific width
      data: { post: this.post }, // Pass the post data to the dialog
    });
    dialogRef.afterClosed().subscribe((result) => {
      if (result) {
        // 'result' will contain the reason and description if the user submitted the form
        console.log('Report Data:', result);
        console.log('Post ID:', this.post.postId);

        // Here's where you would typically call your backend service
        // For example:
        // this.yourApiService.submitReport(this.post.postId, result.reason, result.description)
        //   .subscribe(
        //     response => {
        //       console.log('Report submitted successfully!', response);
        //       // Optionally show a success message to the user
        //     },
        //     error => {
        //       console.error('Error submitting report:', error);
        //       // Optionally show an error message to the user
        //     }
        //   );
        const reportData = {
          postId: this.post.postId,
          reportReason: result.reason,
          reportDescription: result.description,
        };
        this.onReportPost.emit(reportData);
        // For demonstration, let's just log what would be sent
        console.log('Sending to backend:', {
          postId: this.post.postId,
          reason: result.reason,
          description: result.description,
        });
      } else {
        // User cancelled the dialog or closed it without submitting
        console.log('Report dialog closed without submission.');
      }
    });
  }

  savePost(event?: Event): void {
    if (event) {
      event.stopPropagation();
    }
    this.closeMenu();
    this.onSavePost.emit(this.post.postId);
  }

  hidePost(event?: Event): void {
    if (event) {
      event.stopPropagation();
    }
    this.closeMenu();
    this.onHidePost.emit(this.post.postId);
  }

  // Post content formatting
  formatPostDescription(description: string): string {
    if (!description) return '';

    // Convert URLs to clickable links
    const urlRegex = /(https?:\/\/[^\s]+)/g;
    let formatted = description.replace(
      urlRegex,
      '<a href="$1" target="_blank" rel="noopener noreferrer">$1</a>'
    );

    // Convert mentions to styled spans
    const mentionRegex = /@(\w+)/g;
    formatted = formatted.replace(
      mentionRegex,
      '<span class="mention">@$1</span>'
    );

    // Convert hashtags to styled spans
    const hashtagRegex = /#(\w+)/g;
    formatted = formatted.replace(
      hashtagRegex,
      '<span class="hashtag">#$1</span>'
    );

    return formatted;
  }

  // Image modal
  openImageModal(): void {
    if (this.post.postAttachment) {
      this.showImageModal = true;
    }
  }

  closeImageModal(): void {
    this.showImageModal = false;
  }

  // Share poll functionality
  onOpenSharePollFn(): void {
    this.openSharedPostPoll.emit(this.post.postId);
  }

  // Likes functionality
  checkIsLoggedInUserLikedPost(): boolean {
    if (!this.post.postLikes || !this.currentUserId) return false;
    return this.post.postLikes.some(
      (like) => like.userId === this.currentUserId
    );
  }

  likePost(): void {
    const likePost = {
      post: JSON.stringify(this.post),
    };
    this.onPostLike.emit(likePost);
  }

  getTopLikers(): IPostLikes[] {
    if (!this.post.postLikes) return [];

    const enrichedLikes = this.post.postLikes.map((x) => {
      const user = this.userOrgList.find((y) => y.userId === x.userId);
      return {
        ...x,
        userProfile: user?.profilePicture || null,
      };
    });

    return enrichedLikes.slice(0, 3);
  }

  getLikesText(): string {
    if (!this.post.postLikes || this.post.postLikes.length === 0) return '';

    const likeCount = this.post.postLikes.length;
    const currentUserLiked = this.checkIsLoggedInUserLikedPost();

    if (likeCount === 1) {
      return currentUserLiked
        ? 'You like this'
        : `${this.post.postLikes[0].likeUsername} likes this`;
    }

    if (currentUserLiked) {
      if (likeCount === 2) {
        const otherUser = this.post.postLikes.find(
          (like) => like.userId !== this.currentUserId
        );
        return `You and ${otherUser?.likeUsername} like this`;
      }
      return `You and ${likeCount - 1} others like this`;
    }

    return `${likeCount} people like this`;
  }

  // Comments functionality
  getDisplayedComments(): IPostComments[] {
    if (!this.post.postComments) return [];
    return this.showAllComments
      ? this.post.postComments
      : this.post.postComments.slice(0, 3);
  }

  toggleAllComments(): void {
    this.showAllComments = !this.showAllComments;
  }

  likeComment(comment: IPostComments): void {
    // Implement comment like functionality
    console.log('Like comment:', comment.commentId);
  }

  replyToComment(comment: IPostComments): void {
    // Implement reply to comment functionality
    this.commentText = `@${comment.commentUsername} `;
    this.focusTextarea();
  }

  sharePost(): void {
    this.onPostShare.emit(this.post.postId);
  }

  // Comment input functionality
  focusTextarea(): void {
    if (this.commentTextarea) {
      this.commentTextarea.nativeElement.focus();
    }
  }

  onCommentFocus(): void {
    this.isCommentFocused = true;
  }

  onCommentBlur(): void {
    setTimeout(() => {
      if (!this.commentText?.trim()) {
        this.isCommentFocused = false;
      }
    }, 200);
  }

  onKeyDown(event: KeyboardEvent): void {
    if (event.key === 'Enter' && !event.shiftKey) {
      event.preventDefault();
      this.onCommentInput();
    }

    this.adjustTextareaHeight();
  }

  onCommentInputCheck(event: Event): void {
    const target = event.target as HTMLTextAreaElement;
    this.commentText = target.value;
    this.commentInput$.next(this.commentText);
    this.adjustTextareaHeight();
  }

  adjustTextareaHeight(): void {
    if (this.commentTextarea) {
      const textarea = this.commentTextarea.nativeElement;
      textarea.style.height = 'auto';
      const newHeight = Math.min(textarea.scrollHeight, 120);
      this.textareaHeight = Math.max(newHeight, 20);
      textarea.style.height = `${this.textareaHeight}px`;
    }
  }

  onCommentInput(): void {
    if (!this.commentText?.trim()) return;
    // const commentPost = {
    //   isIncident: false,
    //   isVisitor: false,
    //   comment: this.commentText,
    //   commentMentioned:  this.mentionedUsers,
    //     userProfile: localStorage.getItem('ProfilePic')
    //     ? localStorage.getItem('ProfilePic')
    //     : this.userImage,
    //   username: this.loggedIsUsername,
    //   post: JSON.stringify(this.post),
    // };
    // const newComment = {
    //   isAuto: 'false',
    //   postId: this.post.postId,
    //   commentId: this.post.postUserId,
    //   commentText: this.commentText,
    //   commentUsername: commentPost.username,
    //   commentUserId: this.userId,
    //   commentUserProfile: commentPost.userProfile,
    //   commentAddDate: new Date(),
    //   isIncident: 'false',
    //   isVisitor: 'false',
    // };
    this.onPostComment.emit({
      post: JSON.stringify(this.post),
      comment: this.commentText,
      commentMentioned: this.mentionedUsers,
    });

    this.commentText = '';
    this.mentionedUsers = [];
    this.textareaHeight = 20;
    this.isCommentFocused = false;
    this.hideUserSuggestions();
  }

  // User mention functionality
  handleUserMention(text: string): void {
    const cursorPosition =
      this.commentTextarea?.nativeElement.selectionStart || 0;
    const textBeforeCursor = text.substring(0, cursorPosition);
    const mentionMatch = textBeforeCursor.match(/@(\w*)$/);

    if (mentionMatch) {
      const searchTerm = mentionMatch[1].toLowerCase();
      this.mentionStartIndex = cursorPosition - mentionMatch[0].length;

      this.filteredUserOrgList = this.userOrgList.filter((user) => {
        const fullName = `${user.firstname} ${user.lastname}`.toLowerCase();
        return fullName.includes(searchTerm);
      });

      this.showUserList = this.filteredUserOrgList.length > 0;
      this.selectedIndex = 0;
    } else {
      this.hideUserSuggestions();
    }
  }

  handleUserSuggestionNavigation(event: KeyboardEvent): void {
    if (!this.showUserList) return;

    switch (event.key) {
      case 'ArrowDown':
        event.preventDefault();
        this.selectedIndex = Math.min(
          this.selectedIndex + 1,
          this.filteredUserOrgList.length - 1
        );
        break;
      case 'ArrowUp':
        event.preventDefault();
        this.selectedIndex = Math.max(this.selectedIndex - 1, 0);
        break;
      case 'Enter':
      case 'Tab':
        event.preventDefault();
        if (this.filteredUserOrgList[this.selectedIndex]) {
          this.selectUser(this.filteredUserOrgList[this.selectedIndex]);
        }
        break;
      case 'Escape':
        this.hideUserSuggestions();
        break;
    }
  }

  selectUser(user: UserRegDetails): void {
    const textarea = this.commentTextarea?.nativeElement;
    if (!textarea) return;

    const username = `${user.firstname}${user.lastname}`.toLowerCase();
    const beforeMention = this.commentText.substring(0, this.mentionStartIndex);
    const afterCursor = this.commentText.substring(
      textarea.selectionStart || 0
    );

    this.commentText = `${beforeMention}@${username} ${afterCursor}`;

    // Add user to mentioned users list
    if (!this.mentionedUsers.find((u) => u.userId === user.userId)) {
      this.mentionedUsers.push(user);
    }

    this.hideUserSuggestions();

    // Set cursor position after the mention
    setTimeout(() => {
      const newPosition = beforeMention.length + username.length + 2;
      textarea.setSelectionRange(newPosition, newPosition);
      textarea.focus();
    });
  }

  hideUserSuggestions(): void {
    this.showUserList = false;
    this.filteredUserOrgList = [];
    this.selectedIndex = 0;
  }

  // Comment tools
  insertEmoji(): void {
    // Implement emoji picker functionality
    console.log('Insert emoji');
  }

  attachFile(): void {
    // Implement file attachment functionality
    console.log('Attach file');
  }
}
